import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Divider,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { Modal, Button } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const UNIT_OPTIONS = [
  { value: 'Each', label: 'Each' },
  { value: 'Box', label: 'Box' },
  { value: 'Pack', label: 'Pack' },
  { value: 'Set', label: 'Set' },
  { value: 'Bottle', label: 'Bottle' },
  { value: 'Vial', label: 'Vial' },
  { value: 'Tube', label: 'Tube' },
  { value: 'Kit', label: 'Kit' },
  { value: 'Roll', label: 'Roll' },
  { value: 'Pair', label: 'Pair' },
  { value: 'Litre', label: 'Litre' },
  { value: 'Kg', label: 'Kg' },
]

const initialFormData = {
  to_department: '',
  unit_ward: '',
  voucher_date: new Date().toISOString().split('T')[0],
  issued_by_title: '',
  notes: '',
}

const initialLineItem = {
  spare_part: '',
  nature_of_goods: '',
  quantity: 1,
  unit: 'Each',
  folio: '',
  unit_cost: 0,
}

const IssueVoucherForm = ({ open, onClose, voucher, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [lineItems, setLineItems] = useState([{ ...initialLineItem }])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [departments, setDepartments] = useState([])
  const [spareParts, setSpareParts] = useState([])

  const isEditing = Boolean(voucher)

  useEffect(() => {
    if (open) {
      fetchOptions()
      if (voucher) {
        // Load existing voucher data
        setFormData({
          to_department: voucher.to_department || '',
          unit_ward: voucher.unit_ward || '',
          voucher_date: voucher.voucher_date || new Date().toISOString().split('T')[0],
          issued_by_title: voucher.issued_by_title || '',
          notes: voucher.notes || '',
        })
        // Load existing line items if available
        if (voucher.line_items && voucher.line_items.length > 0) {
          setLineItems(voucher.line_items.map(item => ({
            id: item.id,
            spare_part: item.spare_part || '',
            nature_of_goods: item.nature_of_goods || '',
            quantity: item.quantity || 1,
            unit: item.unit || 'Each',
            folio: item.folio || '',
            unit_cost: item.unit_cost || 0,
          })))
        } else {
          setLineItems([{ ...initialLineItem }])
        }
      } else {
        setFormData(initialFormData)
        setLineItems([{ ...initialLineItem }])
      }
      setErrors({})
    }
  }, [open, voucher])

  const fetchOptions = async () => {
    try {
      const [deptRes, partsRes] = await Promise.all([
        api.get('/common/departments/'),
        api.get('/inventory/spare-parts/'),
      ])
      setDepartments(deptRes.data.results || deptRes.data)
      setSpareParts(partsRes.data.results || partsRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }

      // Auto-fill nature_of_goods and unit_cost when spare_part is selected
      if (field === 'spare_part' && value) {
        const selectedPart = spareParts.find(p => p.id === value)
        if (selectedPart) {
          updated[index].nature_of_goods = selectedPart.name
          updated[index].unit_cost = selectedPart.unit_cost || 0
          updated[index].unit = selectedPart.unit_of_measure || 'Each'
        }
      }

      return updated
    })
  }

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { ...initialLineItem }])
  }

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems((prev) => prev.filter((_, i) => i !== index))
    }
  }

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => {
      return sum + (Number(item.quantity) * Number(item.unit_cost))
    }, 0)
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.to_department) newErrors.to_department = 'Section/Unit is required'
    if (!formData.voucher_date) newErrors.voucher_date = 'Date is required'

    // Validate line items
    const hasValidItems = lineItems.some(item => item.nature_of_goods.trim() && item.quantity > 0)
    if (!hasValidItems) {
      newErrors.line_items = 'At least one item with description and quantity is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      const payload = {
        to_department: formData.to_department,
        unit_ward: formData.unit_ward,
        voucher_date: formData.voucher_date,
        issued_by_title: formData.issued_by_title,
        notes: formData.notes,
        line_items: lineItems
          .filter(item => item.nature_of_goods.trim())
          .map(item => ({
            spare_part: item.spare_part || null,
            nature_of_goods: item.nature_of_goods,
            quantity: Number(item.quantity),
            unit: item.unit,
            folio: item.folio,
            unit_cost: Number(item.unit_cost),
          })),
      }

      if (isEditing) {
        await api.patch(`/inventory/issue-vouchers/${voucher.id}/`, payload)
        toast.success('Issue voucher updated successfully')
      } else {
        await api.post('/inventory/issue-vouchers/', payload)
        toast.success('Issue voucher created successfully')
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error saving issue voucher:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          // Handle nested errors (e.g., line_items errors)
          if (Array.isArray(value)) {
            const firstError = value[0]
            if (typeof firstError === 'object' && firstError !== null) {
              // Nested object error - extract the first error message
              const nestedErrors = Object.values(firstError)
              backendErrors[key] = nestedErrors[0] || 'Invalid data'
            } else {
              backendErrors[key] = firstError
            }
          } else if (typeof value === 'object' && value !== null) {
            // Object error - extract first message
            const nestedErrors = Object.values(value)
            backendErrors[key] = nestedErrors[0] || 'Invalid data'
          } else {
            backendErrors[key] = value
          }
        })
        setErrors(backendErrors)
        const errorMessage = error.response?.data?.detail
          || error.response?.data?.error
          || 'Failed to save issue voucher. Please check the form.'
        toast.error(errorMessage)
      } else {
        toast.error('Failed to save issue voucher. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Issue Voucher' : 'Create Issue Voucher'}
      maxWidth="lg"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        {/* Header Information */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={Boolean(errors.to_department)} required>
            <InputLabel>To Section/Unit</InputLabel>
            <Select
              name="to_department"
              value={formData.to_department}
              onChange={handleChange}
              label="To Section/Unit"
            >
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
            {errors.to_department && (
              <Typography variant="caption" color="error">{errors.to_department}</Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Unit/Ward"
            name="unit_ward"
            value={formData.unit_ward}
            onChange={handleChange}
            placeholder="e.g., Oncology Ward"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Voucher Date"
            name="voucher_date"
            type="date"
            value={formData.voucher_date}
            onChange={handleChange}
            error={Boolean(errors.voucher_date)}
            helperText={errors.voucher_date}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Issued By Title"
            name="issued_by_title"
            value={formData.issued_by_title}
            onChange={handleChange}
            placeholder="e.g., Store Keeper"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Line Items
            </Typography>
            <Button size="small" startIcon={<Add />} onClick={addLineItem}>
              Add Item
            </Button>
          </Box>
          {errors.line_items && (
            <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
              {errors.line_items}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell width={50}>#</TableCell>
                  <TableCell width={200}>Spare Part</TableCell>
                  <TableCell>Nature of Goods</TableCell>
                  <TableCell width={80}>Qty</TableCell>
                  <TableCell width={100}>Unit</TableCell>
                  <TableCell width={100}>Folio</TableCell>
                  <TableCell width={120}>Unit Cost</TableCell>
                  <TableCell width={120} align="right">Total</TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={item.spare_part}
                          onChange={(e) => handleLineItemChange(index, 'spare_part', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select or enter manually</em>
                          </MenuItem>
                          {spareParts.map((part) => (
                            <MenuItem key={part.id} value={part.id}>
                              {part.name} ({part.part_number})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={item.nature_of_goods}
                        onChange={(e) => handleLineItemChange(index, 'nature_of_goods', e.target.value)}
                        placeholder="Description"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        inputProps={{ min: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={item.unit}
                          onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                        >
                          {UNIT_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={item.folio}
                        onChange={(e) => handleLineItemChange(index, 'folio', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={item.unit_cost}
                        onChange={(e) => handleLineItemChange(index, 'unit_cost', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      UGX {(Number(item.quantity) * Number(item.unit_cost)).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={7} align="right">
                    <Typography fontWeight="bold">Grand Total:</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      UGX {calculateTotal().toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default IssueVoucherForm
